import { useState, useMemo, useRef } from "react";
import { BsArrowDownUp } from "react-icons/bs";
import axios from "axios";

const UsersTable = ({ currentPosts, setCurrentPosts }) => {
  const [sortColumn, setSortColumn] = useState("");
  const [sortDirection, setSortDirection] = useState("");
  const [columnWidths, setColumnWidths] = useState({
    id: 80,
    userid: 150,
    username: 150,
    status: 120,
  });

  const resizingCol = useRef(null);

  // ✅ Sorting
  const sortedData = useMemo(() => {
    const sortedArray = [...currentPosts];
    if (sortColumn !== "") {
      sortedArray.sort((a, b) => {
        const valueA = a[sortColumn];
        const valueB = b[sortColumn];
        if (sortColumn === "lastmodified") {
          return (new Date(valueA) - new Date(valueB)) * (sortDirection === "asc" ? 1 : -1);
        } else {
          return (valueA < valueB ? -1 : valueA > valueB ? 1 : 0) * (sortDirection === "asc" ? 1 : -1);
        }
      });
    }
    return sortedArray;
  }, [currentPosts, sortColumn, sortDirection]);

  const handleSort = (column) => {
    if (column === sortColumn) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  // ✅ Column resizing logic
  const handleMouseDown = (e, col) => {
    e.preventDefault();
    resizingCol.current = { col, startX: e.clientX, startWidth: columnWidths[col] };
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  const handleMouseMove = (e) => {
    if (!resizingCol.current) return;
    const { col, startX, startWidth } = resizingCol.current;
    const newWidth = Math.max(50, startWidth + (e.clientX - startX));
    setColumnWidths((prev) => ({ ...prev, [col]: newWidth }));
  };

  const handleMouseUp = () => {
    resizingCol.current = null;
    document.removeEventListener("mousemove", handleMouseMove);
    document.removeEventListener("mouseup", handleMouseUp);
  };

  // ✅ Reusable Resizable Header
  const ResizableHeader = ({ colKey, label }) => (
    <th
      className="text-center"
      style={{ width: columnWidths[colKey], position: "relative", userSelect: "none" }}
    >
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ cursor: "pointer" }}
        onClick={() => handleSort(colKey)}
      >
        <BsArrowDownUp className="me-1" /> {label}
      </div>
      {/* Drag border */}
      <div
        onMouseDown={(e) => handleMouseDown(e, colKey)}
        style={{
          cursor: "col-resize",
          position: "absolute",
          right: 0,
          top: 0,
          height: "100%",
          width: "5px",
          zIndex: 5,
        }}
      />
    </th>
  );

  // ✅ Add User (unchanged)
  const [newUser, setNewUser] = useState({ userid: "", username: "", status: "active" });

  const handleAddUser = async (e) => {
    e.preventDefault();
    if (!newUser.userid || !newUser.username) {
      alert("All fields are required.");
      return;
    }

    try {
      const response = await axios.post("/api/users", newUser);
      const createdUser = response.data;
      setCurrentPosts((prev) => [createdUser, ...prev]);
      setNewUser({ userid: "", username: "", status: "active" });
    } catch (error) {
      console.error("Error adding user:", error);
      alert("Failed to add user.");
    }
  };

  return (
    <div style={{ overflowX: "auto" }}>
      <table className="table table-bordered" style={{ minWidth: "600px", borderCollapse: "collapse" }}>
        <thead>
          <tr>
            <ResizableHeader colKey="id" label="ID" />
            <ResizableHeader colKey="userid" label="UserID" />
            <ResizableHeader colKey="username" label="Username" />
            <ResizableHeader colKey="status" label="Status" />
          </tr>
        </thead>
        <tbody>
          {sortedData.map((user, index) => (
            <tr key={index}>
              <td style={{ width: columnWidths.id, fontWeight: "bold"}} className="text-center">
                {user.id}
              </td>
              <td style={{ width: columnWidths.userid }}>{user.userid}</td>
              <td style={{ width: columnWidths.username }}>{user.username}</td>
              <td style={{ width: columnWidths.status }}>{user.status}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default UsersTable;
